import { handleError } from 'common/utils/util.errors';
import React from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { Grid, LinearProgress } from '@mui/material';
import { joinDateAndUser } from 'common/utils/common.util';
import keys from 'common/react-query/data-keys';
import { toast } from 'react-toastify';
import { ROUTES } from 'common/constants/urls';
import { usePrompt } from '../AppPrompt';
import {
  extractMetrics,
  getComparablesLocations,
  getFiltersObject,
  getProjectTypesString,
} from '../construction-cost-report/construction-cost-report.util';
import CCReportSummary from '../construction-cost-report/CCReportSummary';
import CCReportBudgetInfo from '../construction-cost-report/CCReportCostInfo';
import CCReportBudgetPerDivision from '../construction-cost-report/CCReportCostPerDivision';
import { getDivisions } from '../../components/glossary/glossary.api';
import ProjectCompsDialog from '../../components/project-comps/ProjectCompsDialog';
import { GridRowId } from '@mui/x-data-grid';
import CCReportFooter from '../construction-cost-report/CCReportFooter';
import { QCCReport } from './quarterly-cc-report.type';
import { addCompsToQCCReport, deleteQCCReport, getCostPerDivisionForQCC } from './quarterly-cc-report.api';
import { CCReportComp } from '../construction-cost-report/construction-cost-report.type';
import { ReportTypes } from 'common/utils/util.type';
import { Typography } from 'components/index';
import CCComparablesDetails from '../construction-cost-report/CCComparablesDetails';

interface QCCReportRendererProps {
  report: QCCReport;
  updateReport: (updatedReport: QCCReport) => void;
  isReportDirty: boolean;
  isReportLoading: boolean;
  saveReport: () => void;
  undoChanges: () => void;
  refetchReport: () => void;
}

const QCCReportRenderer: React.FC<QCCReportRendererProps> = ({
  report,
  updateReport,
  isReportDirty,
  isReportLoading,
  saveReport,
  undoChanges,
  refetchReport,
}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { getConfirmation } = usePrompt();
  const [isDeleting, setIsDeleting] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [isCompsDialogOpen, setIsCompsDialogOpen] = React.useState(false);
  const [isAddingComps, setIsAddingComps] = React.useState(false);
  const [isEditMode, setIsEditMode] = React.useState(false);
  const [includeSoftCost, setIncludeSoftCost] = React.useState(report.includeSoftCost); // Toggle state

  const toggleEditMode = () => setIsEditMode(val => !val);
  // reset if props change
  React.useEffect(() => {
    if (isEditMode) setIsEditMode(false);
  }, [report]);

  const updateComps = React.useCallback(
    (updatedComps: CCReportComp[]) => {
      const updatedReport = { ...report, comparableProjects: updatedComps };
      updateReport(updatedReport);
    },
    [report, updateReport],
  );

  const open = Boolean(anchorEl);
  // Fetching list of divisions
  const {
    data: divisions,
    isLoading: isLoadingDivisions,
    isError: isErrorDivisions,
    error: errorDivisions,
  } = useQuery(keys.divisions, getDivisions);

  // Fetching cost per division
  const {
    data: costPerDivision,
    isLoading: isLoadingReport,
    isError: isErrorReport,
    error: errorReport,
    refetch: refetchCostPerDivision,
  } = useQuery([report.id], () => getCostPerDivisionForQCC(report.id), {
    enabled: !!report.id,
  });

  if (isLoadingDivisions || isLoadingReport) {
    return <LinearProgress />;
  }

  if (isErrorDivisions || isErrorReport) {
    console.error(errorDivisions || errorReport);
    navigate(ROUTES.NOT_FOUND);
    return null;
  }

  if (!divisions || !costPerDivision) {
    navigate(ROUTES.NOT_FOUND);
    return null;
  }

  const toggleIncludeSoftCost = () => {
    const updatedReport = { ...report, includeSoftCost: !includeSoftCost };
    setIncludeSoftCost(!includeSoftCost);
    updateReport(updatedReport);
  };

  const handleDelete = async () => {
    try {
      if (!report) return;
      const isConfirmed = await getConfirmation({
        heading: 'Delete Report',
        description: `Are you sure you want to delete this report: ${report.name}?`,
        confirmButton: {
          label: 'Delete',
          color: 'error',
        },
        cancelButton: {
          label: 'Cancel',
          color: 'secondary',
        },
      });
      if (!isConfirmed) return;
      setIsDeleting(true);
      await deleteQCCReport(report.id);
      toast.success('Report deleted successfully');
      queryClient.invalidateQueries(keys.qccReports);
      navigate(ROUTES.QCC_REPORTS);
    } catch (error) {
      handleError(error);
    } finally {
      setIsDeleting(false);
    }
  };
  const addComps = async (projects: readonly GridRowId[], filterString?: string) => {
    if (projects.length === 0) {
      toast.error('Please select at least one project to add.');
      return;
    }
    setIsAddingComps(true);
    try {
      if (isReportDirty) {
        const isConfirmed = await getConfirmation({
          description: 'Adding these comps will overwrite any unsaved changes in the Report. Are you sure you want to continue?',
          confirmButton: {
            label: 'Continue',
            color: 'error',
          },
          cancelButton: {
            label: 'Cancel',
            color: 'secondary',
          },
        });
        if (!isConfirmed) return;
      }
      // Extract project IDs from selectedProjectComps
      const selectedProjectIds = projects;

      // Combine both lists of project IDs (avoid duplicates if necessary)
      const combinedProjectIds = Array.from(new Set([...selectedProjectIds]));

      const response = await addCompsToQCCReport({
        ccReportId: report.id,
        projectIds: combinedProjectIds,
      });
      queryClient.invalidateQueries([keys.qccReport, report.id]);
      refetchReport();
      refetchCostPerDivision();
      toast.success('Comps Added successfully!');
      handleCloseCompsDialog();
    } catch (error) {
      handleError(error);
    } finally {
      setIsAddingComps(false);
    }
  };

  const handleOpenCompsDialog = () => {
    setIsCompsDialogOpen(true);
  };
  const handleCloseCompsDialog = () => {
    setIsCompsDialogOpen(false);
  };
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const {
    minGSF,
    maxGSF,
    minUnitCount,
    maxUnitCount,
    minSiteArea,
    maxSiteArea,
    minStoryCount,
    maxStoryCount,
    minBuildingCount,
    maxBuildingCount,
    minBudget,
    maxBudget,
    minBudgetPerGSF,
    maxBudgetPerGSF,
    avgBudgetPerGSF,
    minBudgetPerNSF,
    maxBudgetPerNSF,
    avgBudgetPerNSF,
    minBudgetPerUnit,
    maxBudgetPerUnit,
    avgBudgetPerUnit,
    minPeriodEnding,
    maxPeriodEnding,
  } = extractMetrics(report, includeSoftCost);

  const projectTypesString = getProjectTypesString(report);
  const locations = getComparablesLocations(report);
  const filtersObject = getFiltersObject(report);

  return (
    <>
      <Grid container spacing={2} sx={{ p: 1 }}>
        <Grid size={{ xs: 12 }}>
          <Typography variant='h4' fontWeight='bold' sx={{ textAlign: 'center', marginBottom: 2 }}>
            {report.name}
          </Typography>
        </Grid>
        <Grid size={{ xs: 12 }}>
          <CCReportSummary
            report={report}
            typeOfReport={ReportTypes.QCC}
            updateReport={updateReport}
            filtersObject={filtersObject}
            minGSF={minGSF}
            maxGSF={maxGSF}
            projectTypesString={projectTypesString}
            minBudget={minBudget}
            maxBudget={maxBudget}
            locations={locations}
            minUnitCount={minUnitCount}
            maxUnitCount={maxUnitCount}
            minStoryCount={minStoryCount}
            maxStoryCount={maxStoryCount}
            minPeriodEnding={minPeriodEnding}
            maxPeriodEnding={maxPeriodEnding}
            minBuildingCount={minBuildingCount}
            maxBuildingCount={maxBuildingCount}
            minSiteArea={minSiteArea}
            maxSiteArea={maxSiteArea}
            isEditMode={isEditMode}
            setIsEditMode={setIsEditMode}
            toggleEditMode={toggleEditMode}
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <CCComparablesDetails
            report={report}
            isReportDirty={isReportDirty}
            updateComps={updateComps}
            handleOpenCompsDialog={handleOpenCompsDialog}
            typeOfReport={ReportTypes.QCC}
            includeSoftCost={includeSoftCost}
            toggleIncludeSoftCost={toggleIncludeSoftCost}
          />
        </Grid>
      </Grid>

      <CCReportBudgetInfo
        report={report}
        displayProjections={false}
        maxBudgetPerGSF={maxBudgetPerGSF}
        avgBudgetPerGSF={avgBudgetPerGSF}
        minBudgetPerGSF={minBudgetPerGSF}
        minBudgetPerNSF={minBudgetPerNSF}
        maxBudgetPerNSF={maxBudgetPerNSF}
        avgBudgetPerNSF={avgBudgetPerNSF}
        minBudgetPerUnit={minBudgetPerUnit}
        maxBudgetPerUnit={maxBudgetPerUnit}
        avgBudgetPerUnit={avgBudgetPerUnit}
        displayGraph={true}
        includeSoftCost={includeSoftCost}
        typeOfReport={ReportTypes.QCC}
      />
      <Grid size={{ xs: 12 }} sx={{ p: 1 }}>
        <CCReportBudgetPerDivision
          report={report}
          displayProjections={false}
          divisions={divisions}
          costPerDivision={costPerDivision}
          typeOfReport={ReportTypes.QCC}
          includeSoftCost={includeSoftCost}
        />
      </Grid>
      <CCReportFooter
        report={report}
        isReportDirty={isReportDirty}
        isEditMode={isEditMode}
        isDeleting={isDeleting}
        isReportLoading={isReportLoading}
        anchorEl={anchorEl}
        open={open}
        joinDateAndUser={joinDateAndUser}
        handleDelete={handleDelete}
        saveReport={saveReport}
        handleClick={handleClick}
        handleClose={handleClose}
        handleOpenCompsDialog={handleOpenCompsDialog}
        handleExportAsWord={handleOpenCompsDialog}
      />
      <ProjectCompsDialog
        open={isCompsDialogOpen}
        onClose={handleCloseCompsDialog}
        onSelect={addComps}
        isFromReport={true}
        disabledCompsIds={report.comparableProjects?.map(comp => comp.comp.id)}
        isAdding={isAddingComps}
        prevAppliedFilters={report.filtersString}
      />
    </>
  );
};

export default QCCReportRenderer;
